import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from '../../shared/button.component';
import Select from '../../shared/reactSelect/select.component';
import PeriodDropdown from '../../shared/periodDropdown.component';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import { filterSelectFactory } from '../../utils/dropdownsHelpers';
import {
  allProvisionsPropTypes,
  provisionsProcessedSuccessfullyPropTypes,
  provisionsTrialBalancesNotReceivedPropTypes,
  provisionsTrialBalancesWithErrorsPropTypes,
  provisionsCalculationsWithErrorsPropTypes,
  provisionsOtherErrorsPropTypes,
  journalsPropTypes,
  localJournalsPropTypes,
} from '../../shared/propTypes';
import { renderDate } from '../../shared/dateHelpers';

import {
  fetchBusinessCodes,
  fetchInitialDataForSinglePage,
  fetchProvisionQualityForSingle,
  selectPeriodForSingle,
  selectResourceForSingle,
  selectResourceTypeForSingle,
} from '../store/actions';
import {
  selectResourcesForSelect,
  selectSelectedResource,
  selectProvisionQualityLoading,
} from '../store/selectors';

import QualityDisplay from './qualityDisplay.component';
import ProvisionsGrid from './provisionsGrid.component';
import { resourceTypes } from './provisionQualitySingle.constants';
import { provisionQualityPropTypes } from './provisionQualitySingle.propTypes';

import Routes from '../../routes';
import HamburgerDropdown from './hamburgerDropdown.component';

import { fetchCustomReports } from '../../reports/customReports/store/actions';
import {
  batchProvisionJournalsReportAndReportSetIdsSelector,
} from '../../reports/customReports/store/selectors';
import { updateLocalStorageAndPushCustomReport } from '../../reports/customReports/generateBaseCustomReportLink';

const filterSelectDefault = filterSelectFactory();
const resourceTypesForSelect = [{
  label: resourceTypes.business,
  value: resourceTypes.business,
}, {
  label: resourceTypes.connection,
  value: resourceTypes.connection,
}];

class ProvisionQualitySingle extends PureComponent {
  static propTypes = {
    lastFetch: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    lastRunDate: PropTypes.string.isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    periods: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    resource: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    resourcesForSelect: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ).isRequired,
    resourceType: PropTypes.string.isRequired,
    resourceTypesForSelect: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ).isRequired,

    provisionQualityLoading: PropTypes.bool.isRequired,
    provisionQuality: provisionQualityPropTypes,

    allProvisions: allProvisionsPropTypes,
    processedSuccessfully: provisionsProcessedSuccessfullyPropTypes,
    trialBalancesNotReceived: provisionsTrialBalancesNotReceivedPropTypes,
    trialBalancesWithErrors: provisionsTrialBalancesWithErrorsPropTypes,
    calculationsWithErrors: provisionsCalculationsWithErrorsPropTypes,
    otherErrors: provisionsOtherErrorsPropTypes,
    journals: journalsPropTypes,
    localJournals: localJournalsPropTypes,

    fetchInitialData: PropTypes.func.isRequired,
    fetchProvisionQuality: PropTypes.func.isRequired,
    selectPeriod: PropTypes.func.isRequired,
    selectResource: PropTypes.func.isRequired,
    selectResourceType: PropTypes.func.isRequired,

    fetchCustomReports: PropTypes.func.isRequired,
    batchProvisionJournalsReportIds: PropTypes.shape({
      reportId: PropTypes.string,
      reportSetId: PropTypes.string,
    }),

    match: PropTypes.shape({
      params: PropTypes.shape({
        resource: PropTypes.string,
        resourceType: PropTypes.string,
      }).isRequired,
    }).isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
  };

  componentDidMount() {
    const {
      period,
      resource,
      resourceType,
      fetchInitialData,
      match,
      selectResourceType,
      fetchCustomReports,
    } = this.props;
    fetchCustomReports();
    fetchInitialData({ params: match.params, period, resource, resourceType });
    selectResourceType(match.params.resourceType || resourceType);
  }

  componentDidUpdate(prevProps) {
    const { period, resource, fetchProvisionQuality, history, resourceType } = this.props;

    const areResourcesDifferent
      = resource && prevProps.resource && resource.value !== prevProps.resource.value;
    const arePeriodsDifferent = resource && period && period !== prevProps.period;

    if (areResourcesDifferent || arePeriodsDifferent) {
      const pathname = Routes.dashboard.PROVISION_QUALITY_SINGLE_WITH_PARAMS.replace(
        /:resourceType/,
        resourceType
      ).replace(/:resource/, resource.value);

      history.replace(pathname);
      fetchProvisionQuality(period, resource.value, resourceType);
    }
  }

  selectPeriod = selectedPeriod => {
    const { periods, selectPeriod } = this.props;

    const value = selectedPeriod ? selectedPeriod.value : null;
    const period = periods.find(period => period.name === value);

    selectPeriod(period);
  };

  selectResourceType = selectedResourceType => {
    const { selectResourceType } = this.props;

    selectResourceType(selectedResourceType.value);
  };

  selectResource = selectedResource => {
    const { resource, resourceType, selectResource } = this.props;

    if (!resource || resource.value !== selectedResource.value) {
      selectResource(selectedResource.value, resourceType);
    }
  };

  refresh = () => {
    const { period, resource, resourceType, fetchProvisionQuality } = this.props;
    if (period && resource) {
      fetchProvisionQuality(period, resource.value, resourceType);
    }
  };

  onBatchProvisionJournalClick = () => {
    updateLocalStorageAndPushCustomReport({
      reportSetId: this.props.batchProvisionJournalsReportIds.reportSetId,
      reportId: this.props.batchProvisionJournalsReportIds.reportId,
      history: this.props.history,
      periodName: this.props.period?.name,
      connName: this.props.resource?.value,
    });
  };

  renderAdditionalDropdownItems = () => (
    <button
      className="hamburger-action link-button"
      tabIndex="0"
      onClick={this.onBatchProvisionJournalClick}
      disabled={false}
      key="batchProvisionJournalReport"
    >
      Batch Provision Journal Report
    </button>
  );

  render() {
    const {
      lastFetch,
      lastRunDate,
      period,
      periods,
      provisionQualityLoading,
      provisionQuality,
      resource,
      resourcesForSelect,
      resourceType,
      resourceTypesForSelect,
      allProvisions,
      processedSuccessfully,
      trialBalancesNotReceived,
      trialBalancesWithErrors,
      calculationsWithErrors,
      otherErrors,
      journals,
      localJournals,
    } = this.props;

    const formattedLastFetch = lastFetch ? renderDate(lastFetch) : null;

    const periodsForSelect = periods.map(({ name }) => ({
      value: name,
      label: name,
    }));

    return (
      <div className="provision-quality-single-page">
        <SecondaryMenu>
          <PeriodDropdown disabled={!periodsForSelect.length} onChange={this.selectPeriod} />
          <Select
            clearable={false}
            filterOptions={filterSelectDefault}
            name="resourceType"
            options={resourceTypesForSelect}
            onChange={this.selectResourceType}
            scrollMenuIntoView={false}
            value={resourceType}
            className="select-entry-types"
            virtualized
          />
          <Select
            clearable={false}
            disabled={!period || !resourcesForSelect.length}
            filterOptions={filterSelectDefault}
            name={resourceType}
            options={resourcesForSelect}
            onChange={this.selectResource}
            scrollMenuIntoView={false}
            value={(resource && resource.value) || ''}
            className="select-connection"
            virtualized
          />
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown additionalItems={this.renderAdditionalDropdownItems()} />
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid page-content-container">
            <div className="row">
              <div className="col pb-3">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Provision Quality Detail{resource && ` - ${resource.name}`}</h5>
                  <div>
                    Updated: {formattedLastFetch}{' '}
                    <Button type="button" kind="primary" onClick={this.refresh}>
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {resource && (
              <QualityDisplay
                lastRunDate={lastRunDate}
                name={resource.name}
                provisionQuality={provisionQuality}
                showLastRun={resourceType === resourceTypes.connection}
              />
            )}
          </div>
          <ProvisionsGrid
            allProvisions={allProvisions}
            processedSuccessfully={processedSuccessfully}
            trialBalancesNotReceived={trialBalancesNotReceived}
            trialBalancesWithErrors={trialBalancesWithErrors}
            calculationsWithErrors={calculationsWithErrors}
            otherErrors={otherErrors}
            journals={journals}
            localJournals={localJournals}
            provisionQualityLoading={provisionQualityLoading}
          />
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  period: state.shared.period,
  periods: state.shared.periods,
  resourceType: state.dashboard.provisionQualitySingle.resourceType,
  resource: selectSelectedResource(state),
  resourcesForSelect: selectResourcesForSelect(state),
  resourceTypesForSelect,
  provisionQualityLoading: selectProvisionQualityLoading(state),
  provisionQuality: state.dashboard.provisionQualitySingle.provisionQuality,
  lastFetch: state.dashboard.provisionQualitySingle.lastFetch,
  allProvisions: state.dashboard.provisionQualitySingle.allProvisions,
  processedSuccessfully: state.dashboard.provisionQualitySingle.processedSuccessfully,
  trialBalancesNotReceived: state.dashboard.provisionQualitySingle.trialBalancesNotReceived,
  trialBalancesWithErrors: state.dashboard.provisionQualitySingle.trialBalancesWithErrors,
  calculationsWithErrors: state.dashboard.provisionQualitySingle.calculationsWithErrors,
  otherErrors: state.dashboard.provisionQualitySingle.otherErrors,
  journals: state.dashboard.provisionQualitySingle.journals,
  localJournals: state.dashboard.provisionQualitySingle.localJournals,
  lastRunDate: state.dashboard.provisionQualitySingle.lastRunDate,
  batchProvisionJournalsReportIds: batchProvisionJournalsReportAndReportSetIdsSelector(state),

}), {
  fetchBusinessCodes,
  fetchProvisionQuality: fetchProvisionQualityForSingle,
  fetchInitialData: fetchInitialDataForSinglePage,
  selectPeriod: selectPeriodForSingle,
  selectResource: selectResourceForSingle,
  selectResourceType: selectResourceTypeForSingle,
  fetchCustomReports,
})(ProvisionQualitySingle);
